/*
 * @Author: Ryan
 * @Date: 2025-12-02
 * @Description: Language translation section with supported languages grid
 */
import { Button } from 'antd';
import Image from 'next/image';
import React, { useMemo, useState } from 'react';

import { useTranslationConfig } from '@/hooks/useTranslationConfig';

import StartButton from './StartButton';

interface LanguageCardProps {
  icon?: string;
  name: string;
}

const LanguageCard: React.FC<LanguageCardProps> = ({ icon, name }) => {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-gray-200">
      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden shadow-sm">
        {icon ? (
          <Image
            src={icon}
            alt={name}
            width={40}
            height={40}
            className="w-full h-full object-cover"
            loading="lazy"
            unoptimized
          />
        ) : (
          <div className="w-full h-full bg-gray-300 rounded-full" />
        )}
      </div>
      <span className="text-sm text-gray-800">{name}</span>
    </div>
  );
};

const LanguageSection: React.FC = () => {
  // 使用简单的 mock t 函数获取英文版本
  const [, targetLang] = useTranslationConfig((key: string) => key);

  // 过滤掉没有 icon 的语言
  const languages = useMemo(() => {
    return targetLang.filter((lang) => lang.icon);
  }, [targetLang]);

  const [showAll, setShowAll] = useState(false);

  const visibleLanguages = showAll ? languages : languages.slice(0, 15);

  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 pad:mb-4">
          Translate Video with AI into {languages?.length} Languages Instantly
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto mb-6 pad:mb-8">
          Automatically translate video audio, voices, and subtitles into 35 languages.
          Break language barriers, expand your reach, and share your content with the
          world in just minutes.
        </p>

        {/* CTA Button */}
        <div className="flex justify-center mb-8 pad:mb-12">
          <StartButton />
        </div>

        {/* Languages Grid */}
        <div className="bg-[#FCFCFC] rounded-2xl p-4 pad:p-8 mb-4 pad:mb-6">
          <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-3 pad:gap-4">
            {visibleLanguages.map((lang, index) => (
              <LanguageCard key={index} icon={lang.icon} name={lang.name} />
            ))}
          </div>
        </div>

        {/* See all languages link */}
        {!showAll && (
          <div className="text-center">
            <Button type="link" onClick={() => setShowAll(true)}>
              See All Languages
              <span className="text-xs">&gt;&gt;</span>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default LanguageSection;
